import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  getLanguageManagerAudit,
  syncLanguageJson,
  syncLanguageObjectIds,
} from "../../auth/apiLanguage";
import "./LanguageManagerDashboard.css";
import "./LanguageManagerCompare.css";

const CODES = ["en", "fr", "es", "ht", "pt"];
const LANGUAGE_LABELS = {
  en: { flag: "🇬🇧", label: "English" },
  fr: { flag: "🇫🇷", label: "Français" },
  es: { flag: "🇪🇸", label: "Español" },
  ht: { flag: "🇭🇹", label: "Kreyòl" },
  pt: { flag: "🇵🇹", label: "Português" },
};
const COMPARE_PAGE_SIZE = 100;
const ID_ARRAY_FIELDS = [
  "aboutUs", "errorMessage", "elakayMail", "makeYourFstep", "becomeRoyalty",
  "becomeSeller", "LinkcustomerShop", "becomeProvider", "termConditProdSel",
  "catalogProdSel", "infoProdSel", "admAssPage", "filterProdSel", "imageProdSel",
  "elakayLanguage", "editProduct", "planUserRoyal", "termConditionRoyal", "royalAgree",
  "planUserSeller", "termConditionSeller", "sellerAgree", "planUserProvider",
  "termConditionProvider", "providerAgree", "planProInvestElakay", "termConditionInvest",
  "investmentAgree", "planProAdminElakay", "termConditionAdmin", "AdministratorAgree",
  "planProFamillyElakay", "termConditionFamilly", "FamillyAgree", "planAffaireElakay",
  "termConditionAssociate", "associateAgree", "shippingInfo", "salesSimulation",
  "advertisingEmail", "elakayTicket", "allElakayFile",
];
const SYSTEM_KEYS = new Set(["_id", "__v", "createdAt", "updatedAt", "translator", "supervisor", "Admin", "language"]);

const loadLanguageCatalogues = async () => {
  const modules = await Promise.all([
    import("../../../language/menuElakay_en.json"),
    import("../../../language/menuElakay_fr.json"),
    import("../../../language/menuElakay_es.json"),
    import("../../../language/menuElakay_ht.json"),
    import("../../../language/menuElakay_pt.json"),
  ]);
  return Object.fromEntries(CODES.map((code, index) => [code, modules[index].default[0]]));
};

const flatten = (value, prefix = "", result = new Map()) => {
  if (value === null || typeof value !== "object") {
    if (prefix) result.set(prefix, value);
    return result;
  }
  const keys = Object.keys(value).filter((key) => key !== "_id");
  if (!keys.length) {
    if (prefix) result.set(prefix, value);
    return result;
  }
  keys.forEach((key) => {
    if (!prefix && SYSTEM_KEYS.has(key)) return;
    flatten(value[key], prefix ? `${prefix}.${key}` : key, result);
  });
  return result;
};

const previewValue = (value) => {
  if (typeof value === "string") return value;
  try { return JSON.stringify(value); } catch { return String(value ?? ""); }
};

const managerActionError = (error) => {
  const message = String(error || "").trim();
  if (!message || /not found/i.test(message) || /failed to fetch/i.test(message)) {
    return "L’API Language Manager n’est pas disponible. Redémarrez le serveur backend avant une synchronisation MongoDB.";
  }
  return message;
};

const languageDocument = (documents, code) => documents.find((item) => (
  String(item?.language?.code || "").toLowerCase() === code
)) || {};

const createCompareRows = (documents, catalogues) => {
  if (!catalogues) return [];
  const jsonMaps = Object.fromEntries(CODES.map((code) => [code, flatten(catalogues[code])]));
  const mongoMaps = Object.fromEntries(CODES.map((code) => [code, flatten(languageDocument(documents, code))]));
  const paths = new Set(CODES.flatMap((code) => [...jsonMaps[code].keys(), ...mongoMaps[code].keys()]));
  return [...paths].sort().map((key) => ({
    key,
    values: Object.fromEntries(CODES.map((code) => {
      const mongoHas = mongoMaps[code].has(key);
      const jsonHas = jsonMaps[code].has(key);
      const mongoValue = mongoMaps[code].get(key);
      const jsonValue = jsonMaps[code].get(key);
      return [code, {
        value: mongoHas ? mongoValue : jsonValue,
        mongoValue,
        jsonValue,
        mongoPresent: mongoHas,
        jsonPresent: jsonHas,
        source: mongoHas ? "MongoDB" : jsonHas ? "JSON fallback" : "Missing",
        conflict: mongoHas && jsonHas && JSON.stringify(mongoValue) !== JSON.stringify(jsonValue),
      }];
    })),
  }));
};

const buildLocalAudit = (documents, catalogues) => {
  const rows = createCompareRows(documents, catalogues);
  const totals = {
    jsonKeys: 0, mongoKeys: 0, missing: 0, identical: 0,
    conflicts: 0, mongoOnly: 0, translated: 0, duplicates: 0, missingObjectIds: 0,
  };
  CODES.forEach((code) => {
    totals.jsonKeys += flatten(catalogues[code]).size;
    totals.mongoKeys += flatten(languageDocument(documents, code)).size;
  });
  rows.forEach((row) => CODES.forEach((code) => {
    const cell = row.values[code];
    if (cell.jsonPresent && !cell.mongoPresent) totals.missing += 1;
    if (cell.mongoPresent && !cell.jsonPresent) totals.mongoOnly += 1;
    if (cell.mongoPresent && cell.jsonPresent) {
      totals.translated += 1;
      if (cell.conflict) totals.conflicts += 1;
      else totals.identical += 1;
    }
  }));

  const signatures = new Map();
  const jsonMaps = Object.fromEntries(CODES.map((code) => [code, flatten(catalogues[code])]));
  [...jsonMaps.en.keys()].forEach((key) => {
    if (!CODES.every((code) => jsonMaps[code].has(key) && typeof jsonMaps[code].get(key) === "string")) return;
    const values = Object.fromEntries(CODES.map((code) => [code, jsonMaps[code].get(key)]));
    if (!values.en.trim()) return;
    const signature = JSON.stringify(values);
    if (!signatures.has(signature)) signatures.set(signature, { values, keys: [] });
    signatures.get(signature).keys.push(key);
  });
  const duplicateGroups = [...signatures.values()]
    .filter(({ keys }) => keys.length > 1)
    .map((group) => ({ ...group, occurrences: group.keys.length, mergeSafe: false }))
    .sort((left, right) => right.occurrences - left.occurrences);
  totals.duplicates = duplicateGroups.reduce((sum, group) => sum + group.occurrences - 1, 0);

  let objects = 0;
  let existing = 0;
  let missingIds = 0;
  documents.forEach((document) => ID_ARRAY_FIELDS.forEach((field) => {
    if (!Array.isArray(document?.[field])) return;
    document[field].forEach((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return;
      objects += 1;
      if (item._id) existing += 1;
      else missingIds += 1;
    });
  }));
  totals.missingObjectIds = missingIds;
  return {
    filesAnalyzed: CODES.length,
    totals,
    duplicateGroups: duplicateGroups.slice(0, 100),
    objectIds: { documents: documents.length, objects, existing, missing: missingIds, invalid: 0 },
    localFallback: true,
  };
};

const Modal = ({ title, children, onClose, footer, className = "" }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);
  const content = (
    <div className="lm-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className={`lm-modal ${className}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header><div><span className="lm-eyebrow">ELAKAY LANGUAGE MANAGER</span><h2>{title}</h2></div><button type="button" className="lm-icon-button" onClick={onClose} aria-label="Fermer">×</button></header>
        <div className="lm-modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </section>
    </div>
  );
  return createPortal(content, document.body);
};

const LanguageManagerDashboard = ({ documents = [], onRefresh }) => {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null);
  const [jsonChoices, setJsonChoices] = useState({});
  const [result, setResult] = useState(null);
  const [catalogues, setCatalogues] = useState(null);
  const [compareSearch, setCompareSearch] = useState("");
  const [compareFilter, setCompareFilter] = useState("all");
  const [comparePage, setComparePage] = useState(1);

  const loadAudit = useCallback(async () => {
    setBusy("audit");
    setError("");
    setNotice("");
    const data = await getLanguageManagerAudit();
    if (data?.error) {
      try {
        const localCatalogues = catalogues || await loadLanguageCatalogues();
        setCatalogues(localCatalogues);
        setAudit(buildLocalAudit(documents, localCatalogues));
        setNotice("Statistiques calculées localement. Redémarrez l’API pour utiliser les synchronisations MongoDB.");
      } catch (fallbackError) {
        setError(fallbackError.message || data.error);
      }
    } else setAudit(data);
    setBusy("");
    return data;
  }, [catalogues, documents]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const compareRows = useMemo(() => {
    return createCompareRows(documents, catalogues);
  }, [catalogues, documents]);

  const filteredCompareRows = useMemo(() => {
    const query = compareSearch.trim().toLowerCase();
    return compareRows.filter((row) => {
      const cells = CODES.map((code) => row.values[code]);
      const matchesFilter = compareFilter === "all"
        || (compareFilter === "conflict" && cells.some((cell) => cell.conflict))
        || (compareFilter === "missing" && cells.some((cell) => cell.source === "Missing"))
        || (compareFilter === "mongo" && cells.some((cell) => cell.source === "MongoDB"))
        || (compareFilter === "json" && cells.some((cell) => cell.source === "JSON fallback"));
      if (!matchesFilter) return false;
      if (!query) return true;
      return row.key.toLowerCase().includes(query)
        || cells.some((cell) => previewValue(cell.value).toLowerCase().includes(query));
    });
  }, [compareFilter, compareRows, compareSearch]);

  const comparePageCount = Math.max(1, Math.ceil(filteredCompareRows.length / COMPARE_PAGE_SIZE));
  const visibleCompareRows = filteredCompareRows.slice(
    (comparePage - 1) * COMPARE_PAGE_SIZE,
    comparePage * COMPARE_PAGE_SIZE
  );

  const openCompare = async () => {
    setBusy("compare"); setError("");
    try {
      if (!catalogues) setCatalogues(await loadLanguageCatalogues());
      setComparePage(1);
      setModal("compare");
    } catch (loadError) {
      setError(loadError.message || "Impossible de charger les catalogues JSON.");
    } finally {
      setBusy("");
    }
  };

  const openJsonPreview = async () => {
    setBusy("json"); setError("");
    const data = await syncLanguageJson({ confirm: false });
    setBusy("");
    if (data?.error) return setError(managerActionError(data.error));
    setAudit(data);
    setJsonChoices({});
    setModal("json");
  };

  const applyJson = async () => {
    setBusy("json-apply");
    const choices = Object.entries(jsonChoices).map(([id, source]) => {
      const separator = id.indexOf(":");
      return { code: id.slice(0, separator), key: id.slice(separator + 1), source };
    });
    const data = await syncLanguageJson({ confirm: true, choices });
    setBusy("");
    if (data?.error) return setError(managerActionError(data.error));
    setResult(data); setModal("result");
    await Promise.all([loadAudit(), onRefresh?.()]);
  };

  const openObjectIds = async () => {
    setBusy("ids"); setError("");
    const data = await syncLanguageObjectIds({ confirm: false });
    setBusy("");
    if (data?.error) return setError(managerActionError(data.error));
    setModal({ type: "ids", report: data.objectIds });
  };

  const applyObjectIds = async () => {
    setBusy("ids-apply");
    const data = await syncLanguageObjectIds({ confirm: true });
    setBusy("");
    if (data?.error) return setError(managerActionError(data.error));
    setResult(data); setModal("result");
    await Promise.all([loadAudit(), onRefresh?.()]);
  };

  const refreshAll = async () => {
    await Promise.all([loadAudit(), onRefresh?.()]);
  };

  const totals = audit?.totals || {};
  const stats = [
    ["Total Keys", totals.jsonKeys, "blue"],
    ["Translated", totals.translated, "green"],
    ["Missing", totals.missing, "amber"],
    ["Duplicates", totals.duplicates, "violet"],
    ["Conflicts", totals.conflicts, "red"],
    ["Missing ObjectIds", totals.missingObjectIds, "orange"],
    ["MongoDB Only", totals.mongoOnly, "cyan"],
    ["JSON Only", totals.missing, "slate"],
  ];

  return (
    <section className="lm-dashboard">
      <div className="lm-dashboard-title">
        <div><span className="lm-eyebrow">CENTRE D’ADMINISTRATION MULTILINGUE</span><h2>Elakay Language Manager</h2><p>MongoDB prioritaire · JSON utilisé uniquement comme fallback</p></div>
        <span className={`lm-health ${error ? "is-error" : audit?.localFallback ? "is-local" : ""}`}>{error ? "Action requise" : busy ? "Analyse…" : audit?.localFallback ? "Analyse locale" : "Système prêt"}</span>
      </div>
      {error && <div className="lm-error">{error}</div>}
      {notice && <div className="lm-notice">{notice}</div>}
      <div className="lm-stats">
        {stats.map(([label, value, tone]) => <article key={label} className={`lm-stat lm-${tone}`}><span>{label}</span><strong>{value ?? "—"}</strong></article>)}
      </div>
      <div className="lm-actions" aria-label="Actions Language Manager">
        <button type="button" onClick={loadAudit} disabled={!!busy}>⌁ Analyser</button>
        <button type="button" onClick={openCompare} disabled={!!busy}>⇄ Comparer 5 langues</button>
        <button type="button" onClick={() => setModal("duplicates")} disabled={!audit}>⌘ Doublons</button>
        <span className="lm-action-separator" />
        <button type="button" className="lm-write-action" onClick={openJsonPreview} disabled={!!busy}>↥ Synchroniser JSON → MongoDB</button>
        <button type="button" className="lm-write-action" onClick={openObjectIds} disabled={!!busy}># Synchroniser les ObjectId</button>
        <button type="button" onClick={refreshAll} disabled={!!busy}>↻ Actualiser</button>
      </div>

      {modal === "compare" && <Modal className="lm-modal-wide" title="Comparaison multilingue" onClose={() => setModal(null)}>
        <div className="lm-compare-intro">
          <div><span className="lm-eyebrow">MATRICE DES SOURCES EFFECTIVES</span><p>MongoDB reste prioritaire par champ. Le JSON apparaît uniquement lorsqu’aucune valeur MongoDB n’existe.</p></div>
          <div className="lm-compare-legend"><span className="source-mongo">MongoDB</span><span className="source-json">JSON fallback</span><span className="source-missing">Missing</span><span className="source-conflict">Conflit</span></div>
        </div>
        <div className="lm-compare-toolbar">
          <label className="lm-compare-search"><span>⌕</span><input value={compareSearch} onChange={(event) => { setCompareSearch(event.target.value); setComparePage(1); }} placeholder="Rechercher une clé ou une traduction…" /></label>
          <label className="lm-filter-label">Afficher<select value={compareFilter} onChange={(event) => { setCompareFilter(event.target.value); setComparePage(1); }}><option value="all">Toutes les lignes</option><option value="conflict">Conflits uniquement</option><option value="missing">Traductions manquantes</option><option value="mongo">Source MongoDB</option><option value="json">Source JSON fallback</option></select></label>
          <div className="lm-row-count"><strong>{filteredCompareRows.length.toLocaleString()}</strong><span>résultats</span></div>
        </div>
        <div className="lm-table-wrap"><table className="lm-compare-table"><colgroup><col className="lm-key-column" />{CODES.map((code) => <col key={code} className="lm-language-column" />)}</colgroup><thead><tr><th className="lm-key-heading"><span>Clé de traduction</span><small>Chemin complet</small></th>{CODES.map((code) => <th key={code}><span className="lm-language-heading"><b>{LANGUAGE_LABELS[code].flag}</b><span><strong>{LANGUAGE_LABELS[code].label}</strong><small>{code.toUpperCase()}</small></span></span></th>)}</tr></thead><tbody>
          {visibleCompareRows.map((row) => <tr key={row.key}><th title={row.key}><div className="lm-key-value">{row.key}</div></th>{CODES.map((code) => { const cell = row.values[code]; const value = previewValue(cell.value); const sourceClass = cell.source === "MongoDB" ? "source-mongo" : cell.source === "JSON fallback" ? "source-json" : "source-missing"; return <td key={code} className={cell.conflict ? "has-conflict" : ""}><div className={`lm-cell-value ${value ? "" : "is-empty"}`}>{value || "Valeur vide"}</div><div className="lm-cell-meta"><span className={sourceClass}>{cell.source}</span>{cell.conflict && <span className="source-conflict">Conflit</span>}</div></td>; })}</tr>)}
          {!visibleCompareRows.length && <tr><td className="lm-empty-result" colSpan={6}>Aucun résultat pour cette recherche.</td></tr>}
        </tbody></table></div>
        <div className="lm-compare-footer"><span>Lignes {(comparePage - 1) * COMPARE_PAGE_SIZE + (visibleCompareRows.length ? 1 : 0)}–{(comparePage - 1) * COMPARE_PAGE_SIZE + visibleCompareRows.length} sur {filteredCompareRows.length.toLocaleString()}</span><div><button type="button" disabled={comparePage <= 1} onClick={() => setComparePage((page) => Math.max(1, page - 1))}>← Précédent</button><span>Page <strong>{comparePage}</strong> / {comparePageCount}</span><button type="button" disabled={comparePage >= comparePageCount} onClick={() => setComparePage((page) => Math.min(comparePageCount, page + 1))}>Suivant →</button></div></div>
      </Modal>}

      {modal === "duplicates" && <Modal title="Doublons potentiels" onClose={() => setModal(null)}>
        <p className="lm-help">Les cinq traductions ont été comparées. Aucune fusion n’est automatique : le contexte et les références React doivent être validés avant consolidation.</p>
        <div className="lm-list">{(audit?.duplicateGroups || []).map((group, index) => <article key={`${group.keys[0]}-${index}`}><strong>{group.values.en}</strong><span>{group.occurrences} occurrences</span><code>{group.keys.join(" · ")}</code></article>)}</div>
      </Modal>}

      {modal === "json" && <Modal title="Prévisualisation JSON → MongoDB" onClose={() => setModal(null)} footer={<><button type="button" onClick={() => setModal(null)}>Annuler</button><button type="button" className="lm-confirm" onClick={applyJson} disabled={busy === "json-apply"}>{busy === "json-apply" ? "Synchronisation…" : `Synchroniser ${totals.missing || 0} nouvelles clés`}</button></>}>
        <div className="lm-summary-grid"><span>Fichiers analysés<strong>{audit?.filesAnalyzed}</strong></span><span>Nouvelles clés<strong>{totals.missing}</strong></span><span>Identiques<strong>{totals.identical}</strong></span><span>Conflits<strong>{totals.conflicts}</strong></span></div>
        <p className="lm-help">Par défaut, chaque conflit conserve MongoDB. Seules les clés absentes et les choix JSON explicites seront écrits. Les contenus MongoDB-only ne sont jamais supprimés.</p>
        <div className="lm-conflicts">{(audit?.conflictPreview || []).map((item) => { const id = `${item.code}:${item.key}`; return <article key={id}><div><b>{item.code.toUpperCase()}</b><code>{item.key}</code></div><label><input type="radio" name={id} checked={(jsonChoices[id] || "mongo") === "mongo"} onChange={() => setJsonChoices((value) => ({ ...value, [id]: "mongo" }))} /> MongoDB <span>{previewValue(item.mongoValue)}</span></label><label><input type="radio" name={id} checked={jsonChoices[id] === "json"} onChange={() => setJsonChoices((value) => ({ ...value, [id]: "json" }))} /> JSON <span>{previewValue(item.jsonValue)}</span></label></article>; })}</div>
      </Modal>}

      {modal?.type === "ids" && <Modal title="Prévisualisation des ObjectId" onClose={() => setModal(null)} footer={<><button type="button" onClick={() => setModal(null)}>Annuler</button><button type="button" className="lm-confirm" onClick={applyObjectIds} disabled={!modal.report.missing || busy === "ids-apply"}>{busy === "ids-apply" ? "Synchronisation…" : `Synchroniser ${modal.report.missing} ObjectId`}</button></>}>
        <div className="lm-summary-grid"><span>Documents analysés<strong>{modal.report.documents}</strong></span><span>Objets analysés<strong>{modal.report.objects}</strong></span><span>ObjectId existants<strong>{modal.report.existing}</strong></span><span>ObjectId manquants<strong>{modal.report.missing}</strong></span></div>
        <p className="lm-help">Seuls les sous-documents définis par le schéma Language Manager sont inspectés. Les identifiants existants et tous les textes restent inchangés.</p>
        {modal.report.invalid > 0 && <div className="lm-warning">{modal.report.invalid} identifiant(s) invalide(s) détecté(s). Ils sont signalés mais non remplacés automatiquement afin de protéger les identifiants métier.</div>}
      </Modal>}

      {modal === "result" && <Modal title="Opération terminée" onClose={() => setModal(null)} footer={<button type="button" className="lm-confirm" onClick={() => setModal(null)}>Fermer</button>}><pre className="lm-result">{JSON.stringify(result, null, 2)}</pre></Modal>}
    </section>
  );
};

export default LanguageManagerDashboard;
